import React, { useId } from 'react';
import { Button, Checkbox, InputNumber, Select, Space, Tag } from 'antd';

import { DownOutlined, UpOutlined } from '@ant-design/icons';

import { useGetRecruitmentStatusDictionary } from '../../common/useGetRecruitmentStatusDictionary';

import { MatchingSource } from './matching.types';
import { budgetLabels } from './matchingPresentation';
import { RecommendationSearch } from './useRecommendationSearch';

export const RecommendationsSearchForm = ({
  source,
  search,
}: {
  source: MatchingSource;
  search: RecommendationSearch;
}) => {
  const id = useId();
  const statuses = useGetRecruitmentStatusDictionary();
  const { filters, setFilters } = search;
  return (
    <div className="matching-filters">
      <div className="matching-filter-fields">
        <div className="matching-field">
          <label htmlFor={`${id}-score`}>Minimum fit score</label>
          <InputNumber
            id={`${id}-score`}
            min={0}
            max={100}
            precision={0}
            placeholder="Any score"
            value={filters.minScore}
            onChange={(value) => setFilters({ minScore: value ?? undefined })}
          />
        </div>
        <div className="matching-field">
          <label htmlFor={`${id}-budget`}>Budget</label>
          <Select
            id={`${id}-budget`}
            mode="multiple"
            placeholder="Any budget"
            allowClear
            value={filters.budgetStatus}
            options={Object.entries(budgetLabels).map(([value, label]) => ({ value, label }))}
            onChange={(value) => setFilters({ budgetStatus: value })}
          />
        </div>
        {source.type === 'candidate' && (
          <div className="matching-field">
            <label htmlFor={`${id}-active`}>Project status</label>
            <Select
              id={`${id}-active`}
              value={filters.active === undefined ? 'all' : String(filters.active)}
              options={[
                { value: 'all', label: 'All projects' },
                { value: 'true', label: 'Active projects' },
                { value: 'false', label: 'Inactive projects' },
              ]}
              onChange={(value) => setFilters({ active: value === 'all' ? undefined : value === 'true' })}
            />
          </div>
        )}
      </div>
      <div className="matching-filter-actions">
        <Button
          type="text"
          size="small"
          aria-expanded={search.advanced}
          aria-controls={`${id}-advanced`}
          icon={search.advanced ? <UpOutlined /> : <DownOutlined />}
          onClick={() => search.setAdvanced(!search.advanced)}
        >
          More filters
          {filters.freshness || filters.excludeExistingRecruitment || filters.excludeRecruitmentStatusIds?.length
            ? ' · applied'
            : ''}
        </Button>
        {search.filtered && (
          <Button type="link" size="small" onClick={search.clearFilters}>
            Clear filters
          </Button>
        )}
      </div>
      {search.advanced && (
        <div className="matching-advanced" id={`${id}-advanced`}>
          <div className="matching-filter-fields">
            <div className="matching-field">
              <label htmlFor={`${id}-freshness`}>Assessment freshness</label>
              <Select
                id={`${id}-freshness`}
                value={filters.freshness || 'all'}
                options={[
                  { value: 'all', label: 'All assessments' },
                  { value: 'current', label: 'Current only' },
                  { value: 'stale', label: 'Outdated only' },
                ]}
                onChange={(value) => setFilters({ freshness: value === 'all' ? undefined : value })}
              />
            </div>
            <div className="matching-field">
              <label htmlFor={`${id}-statuses`}>Hide recruitment statuses</label>
              <Select
                id={`${id}-statuses`}
                mode="multiple"
                allowClear
                loading={statuses.isLoading}
                placeholder="None hidden"
                value={filters.excludeRecruitmentStatusIds}
                disabled={!!filters.excludeExistingRecruitment}
                options={statuses.data?.map((status) => ({ value: status.id, label: status.name }))}
                onChange={(value: number[]) => setFilters({ excludeRecruitmentStatusIds: value.slice(0, 100) })}
              />
            </div>
            <Checkbox
              checked={!!filters.excludeExistingRecruitment}
              onChange={(event) => setFilters({ excludeExistingRecruitment: event.target.checked })}
            >
              Hide all existing recruitments
            </Checkbox>
          </div>
          {statuses.isError && (
            <div role="status" className="matching-secondary">
              Recruitment statuses could not be loaded.{' '}
              <Button size="small" type="link" onClick={() => statuses.refetch()}>
                Retry statuses
              </Button>
            </div>
          )}
        </div>
      )}
      {search.filtered && (
        <Space wrap size={[4, 4]} style={{ marginTop: 12 }} aria-label="Applied filters">
          {filters.minScore !== undefined && (
            <Tag closable onClose={() => setFilters({ minScore: undefined })}>
              Fit score ≥ {filters.minScore}
            </Tag>
          )}
          {filters.budgetStatus?.map((value) => (
            <Tag
              key={value}
              closable
              onClose={() => setFilters({ budgetStatus: filters.budgetStatus?.filter((item) => item !== value) })}
            >
              {budgetLabels[value]}
            </Tag>
          ))}
          {filters.active !== undefined && (
            <Tag closable onClose={() => setFilters({ active: undefined })}>
              {filters.active ? 'Active projects' : 'Inactive projects'}
            </Tag>
          )}
          {filters.freshness && (
            <Tag closable onClose={() => setFilters({ freshness: undefined })}>
              {filters.freshness === 'current' ? 'Current assessments' : 'Outdated assessments'}
            </Tag>
          )}
          {filters.excludeExistingRecruitment && (
            <Tag closable onClose={() => setFilters({ excludeExistingRecruitment: undefined })}>
              Existing recruitments hidden
            </Tag>
          )}
          {!filters.excludeExistingRecruitment && !!filters.excludeRecruitmentStatusIds?.length && (
            <Tag closable onClose={() => setFilters({ excludeRecruitmentStatusIds: [] })}>
              {filters.excludeRecruitmentStatusIds.length} recruitment statuses hidden
            </Tag>
          )}
        </Space>
      )}
    </div>
  );
};
